import { h, Host } from "@stencil/core";
import { findAssignedSlot } from "@eventstore-ui/utils";
import { ICON_NAMESPACE } from "../../icons/namespace";
/**
 * Optionally collapsible sectioned view. Each section can be targeted via a part.
 * @slot [sectionName] - Slots are created based off of the names of the passed sections.
 * @part section - Target the outer section.
 * @part [sectionName] - Target the outer section by name.
 * @part section_header - Target the header of each section.
 * @part section_header_title - Target the header text of each section.
 * @part section_header_icon - Target the collapse icon of each section (if collapseable).
 * @part section_content - Target the content wrapper of each section.
 * @part section_content_inner - Target the content of each section.
 */
export class Accordian {
  constructor() {
    this.collapseEls = new Map();
    this.captureElement = (name) => (ref) => {
      if (ref) {
        this.collapseEls.set(name, ref);
      }
      else {
        this.collapseEls.delete(name);
      }
    };
    this.collapseSection = (name) => {
      if (!this.collapseEls.has(name))
        return;
      const element = this.collapseEls.get(name);
      const height = element.scrollHeight;
      element.style.height = `${height}px`;
      element.style.opacity = '1';
      requestAnimationFrame(() => {
        element.style.transitionProperty = 'opacity height';
        element.style.height = '0px';
        element.style.opacity = '0';
      });
      const collapsed = new Set(this.collapsed);
      collapsed.add(name);
      this.collapsed = collapsed;
    };
    this.expandSection = (name) => {
      if (!this.collapseEls.has(name))
        return;
      const element = this.collapseEls.get(name);
      // prevent focusing from messing with the scrolltop
      element.scrollTop = 0;
      const transitionEnd = () => {
        if (!this.collapsed.has(name)) {
          element.style.height = 'auto';
        }
        element.removeEventListener('transitionend', transitionEnd);
      };
      element.addEventListener('transitionend', transitionEnd);
      const height = element.scrollHeight;
      element.style.height = '0px';
      element.style.opacity = '0';
      element.style.transitionProperty = 'opacity height';
      element.style.height = `${height}px`;
      element.style.opacity = '1';
      const collapsed = new Set(this.collapsed);
      collapsed.delete(name);
      this.collapsed = collapsed;
    };
    this.toggleAll = (expand) => {
      this.sections.forEach(({ name, collapsable }) => {
        if (!collapsable)
          return;
        if (expand) {
          this.expandSection(name);
        }
        else {
          this.collapseSection(name);
        }
      });
    };
    this.toggle = (name, collapsable) => (e) => {
      e.preventDefault();
      if (!collapsable)
        return;
      const expand = this.collapsed.has(name);
      if (e.altKey) {
        this.toggleAll(expand);
      }
      else if (expand) {
        this.expandSection(name);
      }
      else {
        this.collapseSection(name);
      }
    };
    this.sections = undefined;
    this.steps = false;
    this.collapsed = new Set();
  }
  componentWillLoad() {
    this.updateCollapsed(this.sections);
  }
  handleFocusIn(e) {
    const target = e.target;
    const slot = findAssignedSlot(target)?.name;
    if (slot && this.collapsed.has(slot)) {
      this.expandSection(slot);
    }
  }
  updateCollapsed(sections) {
    const collapsed = new Set();
    for (const { name, collapsable = false, defaultCollapsed = false, } of sections) {
      if (collapsable && defaultCollapsed) {
        collapsed.add(name);
      }
    }
    this.collapsed = collapsed;
  }
  render() {
    return (h(Host, null, this.sections.map(({ name, title, collapsable = false, variant = 'default', }, i) => {
      const collapsed = this.collapsed.has(name);
      return (h("section", { key: name, id: name, part: `section ${name}`, class: {
          collapsed,
          [variant]: variant !== 'default',
        } }, !!title && (h("header", { id: `${name}-title`, class: {
          ['section_header']: true,
          collapsable,
          collapsed: this.collapsed.has(name),
        }, onClick: this.toggle(name, collapsable), role: collapsable ? 'button' : undefined, "aria-controls": collapsable
          ? `#${name}-content`
          : undefined, "aria-expanded": `${!collapsed}`, part: 'section_header' }, h("h1", { part: 'section_header_title' }, this.steps && (h("es-counter", { variant: 'outline', count: i + 1, class: 'step' })), title), collapsable && (h("es-icon", { part: 'section_header_icon', icon: [
          ICON_NAMESPACE,
          'chevron',
        ], size: 14 })))), h("div", { class: 'section_content', id: `${name}-content`, key: `${name}-content`, "aria-hidden": `${collapsed}`, ref: this.captureElement(name), part: 'section_content' }, h("div", { class: 'section_content_inner', part: 'section_content_inner' }, h("slot", { name: name })))));
    })));
  }
  static get is() { return "es-accordian"; }
  static get encapsulation() { return "shadow"; }
  static get originalStyleUrls() {
    return {
      "$": ["es-accordian.css"]
    };
  }
  static get styleUrls() {
    return {
      "$": ["es-accordian.css"]
    };
  }
  static get properties() {
    return {
      "sections": {
        "type": "unknown",
        "mutable": false,
        "complexType": {
          "original": "AccordianSection[]",
          "resolved": "AccordianSection[]",
          "references": {
            "AccordianSection": {
              "location": "import",
              "path": "./types",
              "id": "src/components/es-accordian/types.ts::AccordianSection"
            }
          }
        },
        "required": true,
        "optional": false,
        "docs": {
          "tags": [],
          "text": "An array of sections to display."
        }
      },
      "steps": {
        "type": "boolean",
        "mutable": false,
        "complexType": {
          "original": "boolean",
          "resolved": "boolean",
          "references": {}
        },
        "required": false,
        "optional": false,
        "docs": {
          "tags": [],
          "text": "Display numbered counters beside each title."
        },
        "attribute": "steps",
        "reflect": false,
        "defaultValue": "false"
      }
    };
  }
  static get states() {
    return {
      "collapsed": {}
    };
  }
  static get watchers() {
    return [{
        "propName": "sections",
        "methodName": "updateCollapsed"
      }];
  }
  static get listeners() {
    return [{
        "name": "focusin",
        "method": "handleFocusIn",
        "target": undefined,
        "capture": false,
        "passive": false
      }];
  }
}
//# sourceMappingURL=es-accordian.js.map
